'use client';

import React from 'react';
import Link from 'next/link';
import { Clock, Users, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocaleRouter } from '@/hooks/useLocaleRouter';
import WishlistButton from './WishlistButton';
import ShareButton from './ShareButton';
import { Title, Text } from '@/components/ui/Headings';

interface JourneyDetailHeroProps {
  journeyId: number;
  image: string;
  imagePosition?: string | null; // CSS object-position value (converted to background-position)
  title: string;
  subtitle: string;
  duration: string;
  groupSize: string;
  startingPrice: string;
  currency: string;
  formatPrice: (price: string, currency: string) => string;
}

export default function JourneyDetailHero({
  journeyId,
  image,
  imagePosition,
  title,
  subtitle,
  duration,
  groupSize,
  startingPrice,
  currency,
  formatPrice,
}: JourneyDetailHeroProps) {
  const { createLocalizedPath } = useLocaleRouter();
  const { t } = useTranslation();
  
  // Get current URL for sharing
  const journeyUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Convert object-position (e.g., "50% 30%") to background-position
  // object-position and background-position use the same format
  const backgroundPosition = imagePosition || 'center center';

  return (
    <section
      className="relative min-h-[350px] sm:min-h-[400px] lg:min-h-[450px] bg-cover"
      style={{ 
        backgroundImage: `url(${image})`,
        backgroundPosition: backgroundPosition
      }}
    >
      <div className="absolute inset-0 bg-black/50"></div>
      
      <div className="relative z-10 h-full flex items-center py-12 sm:py-14 lg:py-16">
        <div className="w-full max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          {/* Top Navigation Bar with Back Button and Action Buttons */}
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <Link
              href={createLocalizedPath("/signature-journeys")}
              className="inline-flex items-center text-white/90 hover:text-white transition-colors text-sm sm:text-base touch-target group"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              <span className="drop-shadow-lg">{t('home.signatureJourney.backToJourneys')}</span>
            </Link>
            
            <div className="flex items-center gap-3 sm:gap-4">
              <ShareButton 
                journeyTitle={title}
                journeyUrl={journeyUrl}
                size="md"
              />
              <WishlistButton journeyId={journeyId} size="md" />
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <Title size="lg" className="text-white mb-2 sm:mb-3 leading-tight">
                {title}
              </Title>
              <Text variant="subtitle" className="text-gray-200 leading-relaxed">{subtitle}</Text>
              <div className="mt-4 sm:mt-5 lg:mt-6 text-white/90">
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-3 sm:mb-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base lg:text-lg">{duration}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base lg:text-lg">{groupSize}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-white/80 text-sm sm:text-base">{t('home.journeyCard.startsFrom')}</span>
                  <span className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white">
                    {formatPrice(startingPrice, currency)}
                  </span>
                </div>
              </div>
            </div>
            <div></div>
          </div>
        </div>
      </div>
    </section>
  );
}

